import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, ScanText, UserRoundPlus } from "lucide-react"
import { type FormEvent, useRef, useState } from "react"
import { useNavigate } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { KpiPersonas } from "@/componentes/kpi/KpiTalento"
import type { Persona } from "@/componentes/personas/FichaPersona"
import { LectorDePersonal } from "@/componentes/personas/LectorDePersonal"
import { extrasDeLaLectura, ResumenLecturaAlta } from "@/componentes/personas/LecturaEnAlta"
import type { Propuesta } from "@/componentes/personas/lectorPersonal"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * El expediente de personas.
 *
 * ⚠ **No es la lista de usuarios, y no debe confundirse con ella.** Aquí está la
 * gente: la plantilla y los contactos de los importadores. La mayoría no entra al
 * sistema —el chofer, el mensajero— y su ficha existe igual; quien sí entra tiene
 * además una cuenta en «Usuarios», enlazada pero aparte. Desactivar la cuenta de
 * quien se va no le borra el expediente, que es todo el motivo de la separación.
 *
 * ⚠ **El listado no enseña dato sensible.** Ni dirección, ni contacto de emergencia,
 * ni notas: una lista se mira en pantalla compartida y se exporta. Eso se ve al
 * abrir la ficha, que es un gesto deliberado sobre una persona concreta.
 *
 * ⚠ **El alta pide el tipo, y no trae uno por defecto.** Es la columna que decide qué
 * campos tienen sentido en toda la ficha; con un valor puesto, la mitad de las fichas
 * acabarían siendo del tipo que vino de fábrica.
 *
 * ⚠ **El catálogo de cargos ya no vive aquí.** Fue una pestaña de esta pantalla y por
 * eso pedía los permisos del expediente; ahora está en Planificación estratégica, con
 * los suyos. Aquí sólo se elige un cargo, y el alta dice dónde se crea el que falta.
 *
 * **El alta es un modal y la ficha un cajón lateral**, como en tareas y en usuarios:
 * dar de alta exige atención completa; revisar a alguien es compararlo con el resto
 * de la lista, que sigue visible al lado.
 */

type Pagina = { total: number; limit: number; offset: number; items: Persona[] }

const TONO_ESTADO = {
  ACTIVO: "exito",
  SUSPENDIDO: "aviso",
  EGRESADO: "neutro",
} as const

/** Cómo se lee cada tipo de ficha en la lista. */
const NOMBRE_TIPO: Record<string, string> = {
  EMPLEADO: "Empleado",
  DIRECTIVA: "Directiva",
  CLIENTE: "Contacto de cliente",
}

const VACIO = {
  tipo: "" as "" | "EMPLEADO" | "DIRECTIVA" | "CLIENTE",
  nombres: "",
  apellidos: "",
  tipo_documento: "V",
  documento: "",
  cargo_id: "",
}

export function Personas() {
  const clienteQuery = useQueryClient()
  const [buscar, setBuscar] = useState("")
  const [tipo, setTipo] = useState("")
  const [soloActivas, setSoloActivas] = useState(true)
  const [abierto, setAbierto] = useState(false)
  const [nueva, setNueva] = useState(VACIO)
  // La lectura IA del alta (19/10): llena el formulario y añade lo que éste no enseña.
  const [leyendoAlta, setLeyendoAlta] = useState(false)
  const [propuesta, setPropuesta] = useState<Propuesta | null>(null)
  const usuario = usarSesion((e) => e.usuario)
  const puedeLeer = tienePermiso(usuario, "persona:leer_ia")
  // (23/10) La ficha es una VISTA PROPIA, el expediente laboral
  // (`paginas/interno/talento/ExpedienteLaboral.tsx`): ya no hay cajón.
  const navegar = useNavigate()
  const abrirFicha = (id: number) => navegar(`/talento-humano/personas/${id}`)

  const consulta = useQuery({
    queryKey: ["personas", { buscar, tipo, soloActivas }],
    queryFn: () => {
      const p = new URLSearchParams({ limit: "100" })
      if (buscar.trim() !== "") p.set("buscar", buscar.trim())
      if (tipo !== "") p.set("tipo", tipo)
      if (soloActivas) p.set("estado", "ACTIVO")
      return api.get<Pagina>(`/api/v1/personas?${p}`)
    },
  })

  const cargosActivos = useQuery({
    queryKey: ["cargos", "activos"],
    queryFn: () => api.get<{ id: number; nombre: string }[]>("/api/v1/cargos?solo_activos=true"),
    enabled: abierto,
  })

  const crear = useMutation({
    mutationFn: () =>
      api.post<Persona>("/api/v1/personas", {
        ...extrasDeLaLectura(propuesta),
        ...nueva,
        // `null` y no cadena vacía, y un NÚMERO: un `<select>` siempre da texto, y
        // mandarlo como cadena daría un 422 sobre un campo elegido de una lista.
        cargo_id: nueva.cargo_id === "" ? null : Number(nueva.cargo_id),
      }),
    onSuccess: (creada) => {
      clienteQuery.invalidateQueries({ queryKey: ["personas"] })
      setAbierto(false)
      setNueva(VACIO)
      setPropuesta(null)
      // Se abre la ficha recién creada: dar de alta a alguien es el principio de
      // llenar su expediente, no el final.
      abrirFicha(creada.id)
    },
  })

  function usarLectura(p: Propuesta) {
    setNueva((n) => ({
      ...n,
      nombres: p.campos.nombres ?? n.nombres,
      apellidos: p.campos.apellidos ?? n.apellidos,
      tipo_documento: p.campos.tipo_documento ?? n.tipo_documento,
      documento: p.campos.documento ?? n.documento,
    }))
    setPropuesta(p)
    setLeyendoAlta(false)
  }

  /** La cédula leída ya tiene ficha: se abre ésa en vez de duplicarla. */
  function abrirExistente(id: number) {
    setAbierto(false)
    setLeyendoAlta(false)
    abrirFicha(id)
  }

  const vista = useRef<HTMLDivElement | null>(null)
  const ETIQUETA_TIPO: Record<string, string> = {
    EMPLEADO: "Empleados",
    DIRECTIVA: "Directiva",
    CLIENTE: "Contactos de cliente",
  }
  const subtitulo = [
    tipo ? (ETIQUETA_TIPO[tipo] ?? tipo) : "Todos los tipos",
    soloActivas ? "Sólo activas" : "Activas e inactivas",
    buscar.trim() !== "" ? `Búsqueda: «${buscar.trim()}»` : null,
    consulta.data ? `${consulta.data.items.length} de ${consulta.data.total}` : null,
  ]
    .filter(Boolean)
    .join(" · ")

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    crear.mutate()
  }

  return (
    // La lista no se imprime: desde aquí sólo sale el carnet, por su pliego.
    <div className="no-print">
      {/* Ni título ni frase propios: los pone la cabecera de «Talento humano». Queda la
          cuenta de fichas, que sí es de esta vista, y la acción a la derecha. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {consulta.data ? `${consulta.data.total} fichas` : " "}
        </p>
        <div className="flex flex-wrap items-start gap-2">
          <Exportar titulo="Personas" subtitulo={subtitulo} contenido={vista} />
          <button
            type="button"
            onClick={() => setAbierto(true)}
            aria-haspopup="dialog"
            className="boton-primario flex h-9 items-center gap-2 px-3 text-sm font-medium"
          >
            <UserRoundPlus size={16} aria-hidden="true" />
            Nueva ficha
          </button>
        </div>
      </div>
      <KpiPersonas />

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <label className="text-sm" htmlFor="buscar-persona">
          Buscar
          <input
            id="buscar-persona"
            className="campo ml-2 inline-block h-9 w-56"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder="Nombre, apellido o cédula"
          />
        </label>
        <label className="flex items-center gap-2 text-sm" htmlFor="filtro-tipo-persona">
          Tipo
          <select
            id="filtro-tipo-persona"
            className="campo h-9 w-auto"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
          >
            <option value="">Todas</option>
            <option value="EMPLEADO">Empleados</option>
            <option value="DIRECTIVA">Directiva</option>
            <option value="CLIENTE">Contactos de cliente</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={soloActivas}
            onChange={(e) => setSoloActivas(e.target.checked)}
          />
          Sólo activas
        </label>
      </div>

      {consulta.isPending && (
        <p role="status" className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      )}

      {consulta.isError && (
        <p
          role="alert"
          className="flex items-start gap-2 py-12 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          {consulta.error instanceof ErrorApi
            ? consulta.error.message
            : "No se pudieron cargar las personas"}
        </p>
      )}

      {consulta.data && (
        <div ref={vista}>
          <Tabla etiqueta="Personas">
            <Cabecera>
              <Th>Nombre</Th>
              <Th>Documento</Th>
              <Th>Cargo</Th>
              <Th>Telefono</Th>
              <Th>Estado</Th>
            </Cabecera>
            <Cuerpo>
              {consulta.data.items.length === 0 ? (
                <TablaVacia colSpan={5}>
                  {buscar.trim() !== "" || tipo !== ""
                    ? "No hay fichas con esos filtros"
                    : "Todavia no hay fichas"}
                </TablaVacia>
              ) : (
                consulta.data.items.map((p) => (
                  <Fila
                    key={p.id}
                    data-testid={`persona-${p.id}`}
                    className="cursor-pointer"
                    onClick={() => abrirFicha(p.id)}
                  >
                    <Td>
                      <span className="block">
                        {p.apellidos}, {p.nombres}
                      </span>
                      <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                        {NOMBRE_TIPO[p.tipo]}
                        {/* Que tenga cuenta se dice aquí: es lo que contesta «¿quién es
                          este usuario?» sin abrir nada. */}
                        {p.usuario_id !== null && " · con acceso"}
                      </span>
                    </Td>
                    <Td numero>
                      {p.tipo_documento}-{p.documento}
                    </Td>
                    <Td>{p.cargo ?? "—"}</Td>
                    <Td numero>{p.telefono ?? "—"}</Td>
                    <Td>
                      <Insignia tono={TONO_ESTADO[p.estado]}>{p.estado}</Insignia>
                    </Td>
                  </Fila>
                ))
              )}
            </Cuerpo>
          </Tabla>
        </div>
      )}

      <Modal
        abierto={abierto}
        titulo="Nueva ficha"
        descripcion="Una persona existe sin cuenta: la mayoria de la plantilla no entra al sistema."
        ancho={leyendoAlta ? "lg" : "md"}
        onCerrar={() => {
          setAbierto(false)
          setLeyendoAlta(false)
        }}
      >
        {leyendoAlta ? (
          <LectorDePersonal
            actual={(campo) => String((nueva as Record<string, unknown>)[campo] ?? "")}
            onUsar={usarLectura}
            onCerrar={() => setLeyendoAlta(false)}
            onAbrirFicha={abrirExistente}
          />
        ) : (
          <form onSubmit={enviar} aria-label="Nueva ficha">
            {propuesta !== null ? (
              <ResumenLecturaAlta propuesta={propuesta} onDescartar={() => setPropuesta(null)} />
            ) : (
              puedeLeer && (
                <button
                  type="button"
                  onClick={() => setLeyendoAlta(true)}
                  className="boton-secundario mb-4 inline-flex h-9 items-center gap-2 px-3 text-sm"
                >
                  <ScanText size={16} aria-hidden={true} />
                  Llenar con la cédula, el RIF y el currículo (IA)
                </button>
              )
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label htmlFor="n-tipo" className="mb-1 block text-xs font-medium uppercase">
                  Tipo de ficha
                </label>
                <select
                  id="n-tipo"
                  required
                  className="campo"
                  value={nueva.tipo}
                  onChange={(e) =>
                    setNueva({ ...nueva, tipo: e.target.value as typeof nueva.tipo })
                  }
                >
                  <option value="">Elige uno…</option>
                  <option value="EMPLEADO">Empleado</option>
                  <option value="DIRECTIVA">Directiva</option>
                  <option value="CLIENTE">Contacto de un cliente</option>
                </select>
              </div>

              <div>
                <label htmlFor="n-nombres" className="mb-1 block text-xs font-medium uppercase">
                  Nombres
                </label>
                <input
                  id="n-nombres"
                  required
                  minLength={2}
                  className="campo"
                  value={nueva.nombres}
                  onChange={(e) => setNueva({ ...nueva, nombres: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="n-apellidos" className="mb-1 block text-xs font-medium uppercase">
                  Apellidos
                </label>
                <input
                  id="n-apellidos"
                  required
                  minLength={2}
                  className="campo"
                  value={nueva.apellidos}
                  onChange={(e) => setNueva({ ...nueva, apellidos: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="n-tipo-doc" className="mb-1 block text-xs font-medium uppercase">
                  Documento
                </label>
                <select
                  id="n-tipo-doc"
                  className="campo"
                  value={nueva.tipo_documento}
                  onChange={(e) => setNueva({ ...nueva, tipo_documento: e.target.value })}
                >
                  {["V", "E", "P", "J", "G"].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="n-documento" className="mb-1 block text-xs font-medium uppercase">
                  Número
                </label>
                <input
                  id="n-documento"
                  required
                  minLength={4}
                  className="campo"
                  value={nueva.documento}
                  onChange={(e) => setNueva({ ...nueva, documento: e.target.value })}
                />
              </div>

              {/* Sólo para empleados: un contacto de importador no tiene cargo, y el
                servidor rechaza la ficha si llega con él. */}
              {(nueva.tipo === "EMPLEADO" || nueva.tipo === "DIRECTIVA") && (
                <div className="md:col-span-2">
                  <label htmlFor="n-cargo" className="mb-1 block text-xs font-medium uppercase">
                    Cargo
                  </label>
                  {/* Del catálogo, y sólo los activos: lo retirado no se le asigna a
                    nadie nuevo aunque siga explicando qué hacía quien lo tuvo. */}
                  <select
                    id="n-cargo"
                    className="campo"
                    value={nueva.cargo_id}
                    onChange={(e) => setNueva({ ...nueva, cargo_id: e.target.value })}
                  >
                    <option value="">Sin cargo</option>
                    {(cargosActivos.data ?? []).map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.nombre}
                      </option>
                    ))}
                  </select>
                  {/* Texto y no enlace: salir de aquí perdería lo tecleado en el alta. */}
                  <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                    ¿No está el puesto? Los cargos se crean en Planificación estratégica › Cargos.
                  </p>
                </div>
              )}
            </div>

            {crear.error && (
              <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
                {crear.error instanceof ErrorApi
                  ? crear.error.message
                  : "No se pudo crear la ficha"}
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setAbierto(false)}
                className="boton-secundario h-9 px-3 text-sm"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={crear.isPending}
                className="boton-primario h-9 px-3 text-sm"
              >
                Crear ficha
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
